import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, Send, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * Reusable "Email this report" button + dialog. Works for ANY downloadable
 * report — just pass its internal download URL.
 *
 * Props:
 *  - reportUrl: internal API path (e.g. "/api/invoices/{id}/download?format=pdf")
 *  - filename:  suggested attachment filename
 *  - defaultSubject / defaultMessage
 *  - label:     button text (default "Email")
 *  - iconOnly:  render just the mail icon
 */
export const SendReportButton = ({
  reportUrl,
  filename = 'report.pdf',
  defaultSubject = 'Report',
  defaultMessage = '',
  label = 'Email',
  iconOnly = false,
  variant = 'outline',
  size = 'sm',
  className = '',
  testid = 'send-report-btn',
}) => {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);
  const [recipients, setRecipients] = useState([]);
  const [autoCc, setAutoCc] = useState('hr@starsnbars.ae');
  const [sending, setSending] = useState(false);

  const openDialog = async () => {
    setSubject(defaultSubject);
    setMessage(defaultMessage || `Please find the attached report: ${filename}`);
    setTo('');
    setOpen(true);
    try {
      const r = await axios.get(`${API}/api/report-email/recipients`, { headers: authHeaders() });
      setRecipients(r.data?.recipients || []);
      if (r.data?.auto_cc) setAutoCc(r.data.auto_cc);
    } catch {
      setRecipients([]);
    }
  };

  const addSaved = (email) => {
    if (!email) return;
    const parts = to.split(',').map(s => s.trim()).filter(Boolean);
    if (!parts.includes(email)) parts.push(email);
    setTo(parts.join(', '));
  };

  const send = async () => {
    const list = to.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    if (list.length === 0) {
      toast({ title: 'Add at least one recipient', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/report-email/send`, {
        report_url: reportUrl,
        filename,
        to: list,
        subject,
        message,
      }, { headers: authHeaders() });
      toast({ title: 'Report emailed', description: `To ${r.data.to.join(', ')} · cc ${r.data.cc}` });
      setOpen(false);
    } catch (e) {
      toast({ title: 'Send failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={openDialog}
        className={className}
        data-testid={testid}
        title="Email this report"
      >
        <Mail className={`h-3.5 w-3.5 ${iconOnly ? '' : 'mr-1'}`} />
        {!iconOnly && label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="send-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-indigo-600" /> Email Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Send to *</Label>
              <Input
                value={to}
                onChange={e => setTo(e.target.value)}
                placeholder="name@example.com (comma-separate for multiple)"
                data-testid="report-email-to"
              />
              {recipients.length > 0 && (
                <div className="mt-2">
                  <Select onValueChange={addSaved} value="">
                    <SelectTrigger className="h-8 text-xs" data-testid="report-email-saved-select">
                      <SelectValue placeholder="+ Add from saved recipients" />
                    </SelectTrigger>
                    <SelectContent>
                      {recipients.map(em => (
                        <SelectItem key={em} value={em}>{em}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="text-xs text-gray-500 bg-gray-50 rounded-md px-3 py-2">
              CC (always): <span className="font-medium text-gray-700">{autoCc}</span>
              <span className="block text-[11px] text-gray-400">New recipients are saved for quick re-use (first 5). Manage them in Settings → Operations.</span>
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="report-email-subject" />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} data-testid="report-email-message" />
            </div>
            <div className="text-[11px] text-gray-400">📎 Attachment: {filename}</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={send} disabled={sending} className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-testid="report-email-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SendReportButton;
